import React, { useState, useEffect, useCallback, useRef } from "react";
import cx from "classnames";
import {
  setEnabled,
  setStackTraces,
  getStoreStats,
  getStoreSubscribers,
  getActionLog,
  clearActionLog,
  enableAndPersist,
  disableAndPersist,
  wasEnabledAtBoot,
  getPinnedStores,
  setPinnedStores,
  getActionsExpanded,
  setActionsExpanded,
  getActionsPanelWidth,
  setActionsPanelWidth,
} from "../../../lib/reflux/monitoring";
import { getStoreByName, STORE_INTERNAL_PROPS } from "../../../lib/reflux";
import formatTime from "../formatTime";
import styles from "./RefluxMonitoring.module.css";
import SubscriberRow from "./SubscriberRow";
import JsonTree from "./JsonTree";
import EnableMonitoringPanel from "./EnableMonitoringPanel";
import ActionsPanel from "./ActionsPanel";

/**
 * Format age in human-readable format.
 * @param {number} ms - Age in milliseconds
 * @returns {string} Formatted age string
 */
const formatAge = (ms) => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  return `${Math.round(ms / 3600000)}h`;
};


/**
 * Check if value is an Immutable.js object.
 * @param {*} value - Value to check
 * @returns {boolean} True if value is an Immutable.js object
 */
const isImmutable = (value) => {
  return value && typeof value.toJS === "function";
};

/**
 * Reflux monitoring panel for DevTools.
 * 3-column layout: Stores | State | Subscribers
 */
const RefluxMonitoring = () => {
  // Check if monitoring was enabled at boot
  const enabledAtBoot = wasEnabledAtBoot();
  const [stats, setStats] = useState({ stores: [], total: 0 });
  const [selectedStore, setSelectedStore] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [storeState, setStoreState] = useState(null);
  const [immutableProps, setImmutableProps] = useState(new Set());
  const [actionLogData, setActionLogData] = useState([]);
  const [storeFilter, setStoreFilter] = useState("");
  const [pinnedStores, setPinnedStoresState] = useState(() => new Set(getPinnedStores()));
  const [hoveredStore, setHoveredStore] = useState(null);
  const [actionsExpanded, setActionsExpandedState] = useState(() => getActionsExpanded());
  const [actionsPanelWidth, setActionsPanelWidthState] = useState(() => getActionsPanelWidth());
  const [detailView, setDetailView] = useState("state"); // 'state' | 'subscribers'
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);

  // Close settings menu when clicking outside
  useEffect(() => {
    if (!settingsOpen) return;

    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [settingsOpen]);

  // Auto-enable monitoring when component mounts
  useEffect(() => {
    setEnabled(true);
    setStackTraces(true);
  }, []);

  // Save pinned stores to localStorage when they change
  useEffect(() => {
    setPinnedStores([...pinnedStores]);
  }, [pinnedStores]);

  // Toggle pin state for a store
  const togglePin = (storeName, e) => {
    e.stopPropagation(); // Don't trigger store selection
    setPinnedStoresState((prev) => {
      const next = new Set(prev);
      if (next.has(storeName)) {
        next.delete(storeName);
      } else {
        next.add(storeName);
      }
      return next;
    });
  };

  // Fetch store state on demand - gathers all enumerable properties
  const fetchStoreState = useCallback((storeName) => {
    const store = getStoreByName(storeName);
    if (!store) {
      setStoreState(null);
      setImmutableProps(new Set());
      return;
    }

    const stateObj = {};
    const immutableKeys = new Set();

    for (const key of Object.keys(store)) {
      if (STORE_INTERNAL_PROPS.has(key)) continue;
      if (typeof store[key] === "function") continue;

      const value = store[key];
      if (isImmutable(value)) {
        immutableKeys.add(key);
        stateObj[key] = value.toJS();
      } else {
        stateObj[key] = value;
      }
    }

    setStoreState(Object.keys(stateObj).length > 0 ? stateObj : null);
    setImmutableProps(immutableKeys);
  }, []);

  // Refresh stats
  const refresh = useCallback(() => {
    const newStats = getStoreStats();
    setStats(newStats);
    if (selectedStore) {
      setSubscribers(getStoreSubscribers(selectedStore));
      fetchStoreState(selectedStore);
    }
    // Always fetch action log since ActionsPanel is always visible
    setActionLogData(getActionLog());
  }, [selectedStore, fetchStoreState]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-poll every 1.5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refresh();
    }, 1500);

    return () => clearInterval(interval);
  }, [refresh]);

  // Handle store click - fetch both state and subscribers
  const handleStoreClick = (storeName) => {
    setSelectedStore(storeName);
    setSubscribers(getStoreSubscribers(storeName));
    fetchStoreState(storeName);
  };

  // Handle clear action log
  const handleClearActionLog = () => {
    clearActionLog();
    setActionLogData([]);
  };

  // Handle actions panel toggle
  const handleActionsToggle = () => {
    const newExpanded = !actionsExpanded;
    setActionsExpandedState(newExpanded);
    setActionsExpanded(newExpanded);
  };

  // Handle actions panel width change
  const handleActionsPanelWidthChange = (width) => {
    setActionsPanelWidthState(width);
    setActionsPanelWidth(width);
  };

  // Handle enable monitoring
  const handleEnableMonitoring = () => {
    enableAndPersist();
    window.location.reload();
  };

  // Handle disable monitoring
  const handleDisableMonitoring = () => {
    disableAndPersist();
    window.location.reload();
  };

  // If monitoring was not enabled at boot, show enable panel
  if (!enabledAtBoot) {
    return <EnableMonitoringPanel onEnable={handleEnableMonitoring} />;
  }

  return (
    <div className={styles.container}>
      {/* Main Layout - Content Columns */}
      <div className={styles.mainLayout}>
        {/* Left Panel - Store List */}
        <div className={styles.leftPanel}>
          <div className={styles.leftPanelHeader}>
            <span>Stores</span>
            <div className={styles.settingsWrapper} ref={settingsRef}>
              <button
                type="button"
                className={styles.settingsBtn}
                onClick={() => setSettingsOpen(!settingsOpen)}
                title="Settings"
              >
                ⚙
              </button>
              {settingsOpen && (
                <div className={styles.settingsMenu}>
                  <div
                    className={styles.settingsMenuItem}
                    onClick={handleDisableMonitoring}
                    onKeyDown={(e) => e.key === "Enter" && handleDisableMonitoring()}
                    role="menuitem"
                    tabIndex={0}
                  >
                    Disable Monitoring
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={styles.filterRow}>
            <input
              type="text"
              placeholder="Filter stores..."
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value)}
              className={styles.filterInput}
            />
            <span className={styles.subsCount}>
              Subs <span className={styles.accentColor}>{stats.total}</span>
            </span>
          </div>
          <div className={styles.storeList}>
            {stats.stores.length === 0 ? (
              <div className={styles.emptyState}>No stores tracked</div>
            ) : (
              stats.stores
                .filter((store) => store.name.toLowerCase().includes(storeFilter.toLowerCase()))
                .slice() // Create new array so React sees changes when re-sorting
                .sort((a, b) => {
                  const aPinned = pinnedStores.has(a.name);
                  const bPinned = pinnedStores.has(b.name);
                  if (aPinned && !bPinned) return -1;
                  if (!aPinned && bPinned) return 1;
                  return b.count - a.count; // Keep original sort by count within groups
                })
                .map((store) => (
                <div
                  key={store.name}
                  className={cx(styles.storeItem, { [styles.storeItemSelected]: selectedStore === store.name })}
                  onClick={() => handleStoreClick(store.name)}
                  onKeyDown={(e) => e.key === "Enter" && handleStoreClick(store.name)}
                  onMouseEnter={() => setHoveredStore(store.name)}
                  onMouseLeave={() => setHoveredStore(null)}
                  title={store.oldestAge ? `Oldest: ${formatAge(store.oldestAge)}` : undefined}
                  role="button"
                  tabIndex={0}
                >
                  {/* Pin icon gutter - fixed width so names stay aligned */}
                  <span className={styles.pinGutter}>
                    {(pinnedStores.has(store.name) || hoveredStore === store.name) && (
                      <span
                        onClick={(e) => togglePin(store.name, e)}
                        onKeyDown={(e) => e.key === "Enter" && togglePin(store.name, e)}
                        className={styles.pinIcon}
                        style={{
                          color: pinnedStores.has(store.name)
                            ? selectedStore === store.name
                              ? "#fff"
                              : "#f59e0b"
                            : "var(--text-base-tertiary)",
                        }}
                        title={pinnedStores.has(store.name) ? "Unpin store" : "Pin store"}
                        role="button"
                        tabIndex={0}
                      >
                        {pinnedStores.has(store.name) ? "●" : "○"}
                      </span>
                    )}
                  </span>
                  <span className={styles.storeName}>{store.name}</span>
                  <span className={cx(styles.storeCount, { [styles.storeCountSelected]: selectedStore === store.name })}>
                    {store.count}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Detail Panel - State or Subscribers */}
        <div className={styles.middlePanel}>
          {/* Panel Header with store name and toggle */}
          <div className={styles.detailPanelHeader}>
            {selectedStore ? (
              <>
                <span className={styles.selectedStoreName}>{selectedStore}</span>
                <div className={styles.detailToggle}>
                  <button
                    type="button"
                    className={cx(styles.detailToggleBtn, { [styles.detailToggleBtnActive]: detailView === "state" })}
                    onClick={() => setDetailView("state")}
                  >
                    State
                  </button>
                  <button
                    type="button"
                    className={cx(styles.detailToggleBtn, { [styles.detailToggleBtnActive]: detailView === "subscribers" })}
                    onClick={() => setDetailView("subscribers")}
                  >
                    Subscribers
                  </button>
                </div>
              </>
            ) : (
              <span className={styles.detailPanelPlaceholder}>Select a store</span>
            )}
          </div>
          <div className={styles.contentPanel}>
            {!selectedStore ? (
              <div className={styles.emptyState}>Select a store</div>
            ) : detailView === "state" ? (
              // State View
              storeState === null ? (
                <div className={styles.emptyState}>No state properties</div>
              ) : (
                <div className={styles.stateContent}>
                  {Object.keys(storeState).map((key) => (
                    <div key={key} className={styles.stateProperty}>
                      <span className={styles.jsonTreeKey}>{key}</span>
                      {immutableProps.has(key) && (
                        <span className={styles.immutableBadge}>Imm</span>
                      )}
                      <span className={styles.statePropertyColon}>: </span>
                      <JsonTree data={storeState[key]} depth={1} />
                    </div>
                  ))}
                </div>
              )
            ) : (
              // Subscribers View
              subscribers.length === 0 ? (
                <div className={styles.emptyState}>No subscribers</div>
              ) : (
                <>
                  <div className={styles.subscriberHeader}>
                    <span className={styles.colAge}>Age</span>
                    <span className={styles.colLocation}>Subscribed From</span>
                    <span className={styles.subscriberCount}>{subscribers.length} total</span>
                  </div>
                  {subscribers.map((sub) => (
                    <SubscriberRow
                      key={sub.id}
                      sub={sub}
                      formatAge={formatAge}
                    />
                  ))}
                </>
              )
            )}
          </div>
        </div>

        {/* Actions Panel - Collapsible Right Column */}
        <ActionsPanel
          actions={actionLogData}
          expanded={actionsExpanded}
          onToggle={handleActionsToggle}
          onClear={handleClearActionLog}
          formatTime={formatTime}
          width={actionsPanelWidth}
          onWidthChange={handleActionsPanelWidthChange}
        />
      </div>
    </div>
  );
};

export default RefluxMonitoring;
